import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Send,
  CheckCircle,
  AlertCircle,
  FileText,
  Star
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    service_type: '',
    message: '',
    is_urgent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success('Message envoyé avec succès !', {
          description: 'Nous vous recontacterons dans les plus brefs délais.'
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          service_type: '',
          message: '',
          is_urgent: false
        });
      } else {
        throw new Error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi', {
        description: 'Veuillez réessayer ou nous contacter par téléphone.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-[#F8FAFC] py-16 md:py-20" data-testid="contact-hero">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="badge-primary mb-4">Contact</span>
            <h1 className="font-outfit text-4xl md:text-5xl font-bold text-[#000000] mt-4 mb-6">
              Contactez-nous
            </h1>
            <p className="text-slate-600 text-lg">
              Une question, un projet ou une urgence ? Notre équipe est à votre écoute. 
              Contactez-nous par téléphone, email ou via le formulaire ci-dessous.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="py-6 bg-[#fabc63]" data-testid="contact-emergency">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
            <div className="flex items-center gap-2">
              <AlertCircle size={24} className="text-black" />
              <span className="font-bold text-black text-lg">Urgence 24h/24 - 7j/7</span>
            </div>
            <a 
              href="tel:0603054911"
              className="bg-black text-white px-8 py-3 rounded-full font-bold hover:bg-slate-800 transition-colors flex items-center gap-2 text-lg"
              data-testid="contact-emergency-call"
            >
              <Phone size={20} />
              06 03 05 49 11
            </a>
          </div>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="section-padding bg-white" data-testid="contact-main">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <motion.div 
              className="lg:col-span-1 space-y-6"
              {...fadeInUp}
            >
              <h2 className="font-outfit text-2xl font-bold text-[#000000] mb-6">
                Nos coordonnées
              </h2>
              
              {/* Phone - highlighted */}
              <a 
                href="tel:0603054911"
                className="flex items-start gap-4 p-5 bg-gradient-to-r from-[#fabc63]/20 to-[#fabc63]/5 rounded-2xl hover:shadow-md transition-all group border-2 border-[#fabc63]/30"
                data-testid="contact-info-phone"
              >
                <div className="w-14 h-14 bg-[#fabc63] rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Phone className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="font-outfit font-semibold text-[#000000] mb-1">Téléphone</h3>
                  <p className="text-[#1997b0] font-bold text-xl">06 03 05 49 11</p>
                  <p className="text-sm text-[#fabc63] font-semibold mt-1">Urgences 24h/24 - 7j/7</p>
                </div>
              </a>

              {/* Email */}
              <a 
                href="mailto:admconfortclim@gmail.com"
                className="flex items-start gap-4 p-5 bg-[#F8FAFC] rounded-2xl hover:bg-[#1997b0]/10 hover:shadow-md transition-all group"
                data-testid="contact-info-email"
              >
                <div className="w-14 h-14 bg-[#1997b0] rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Mail className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="font-outfit font-semibold text-[#000000] mb-1">Email</h3>
                  <p className="text-[#1997b0] font-medium">admconfortclim@gmail.com</p>
                </div>
              </a>

              {/* Address */}
              <div className="flex items-start gap-4 p-5 bg-[#F8FAFC] rounded-2xl">
                <div className="w-14 h-14 bg-[#1997b0] rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="font-outfit font-semibold text-[#000000] mb-1">Adresse</h3>
                  <p className="text-slate-600">
                    209 rue de Changis<br />
                    77550 Moissy-Cramayel<br />
                    <span className="text-sm text-slate-500">Seine-et-Marne, France</span>
                  </p>
                </div>
              </div>

              {/* Hours */}
              <div className="flex items-start gap-4 p-5 bg-[#F8FAFC] rounded-2xl">
                <div className="w-14 h-14 bg-[#1997b0] rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="font-outfit font-semibold text-[#000000] mb-1">Horaires</h3>
                  <p className="text-slate-600">
                    Lundi - Vendredi : 8h - 19h<br />
                    Samedi : Sur rendez-vous<br />
                    <span className="text-[#fabc63] font-semibold">Urgences : 24h/24 - 7j/7</span>
                  </p>
                </div>
              </div>

              {/* Quick CTA buttons */}
              <div className="pt-4 space-y-3">
                <a 
                  href="tel:0603054911"
                  className="btn-emergency w-full flex items-center justify-center gap-2"
                  data-testid="contact-cta-urgent"
                >
                  <Phone size={18} />
                  Appel urgent
                </a>
                <button
                  onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="contact-cta-devis"
                >
                  <FileText size={18} />
                  Demander un devis
                </button>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div 
              className="lg:col-span-2"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div id="contact-form" className="bg-[#F8FAFC] p-8 md:p-10 rounded-2xl">
                <h2 className="font-outfit text-2xl font-bold text-[#000000] mb-2">
                  Demande de devis gratuit
                </h2>
                <p className="text-slate-600 mb-8">
                  Remplissez le formulaire ci-dessous et nous vous recontacterons dans les plus brefs délais.
                </p>

                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="text-green-600" size={40} />
                    </div>
                    <h3 className="font-outfit text-2xl font-semibold text-[#000000] mb-3">
                      Message envoyé !
                    </h3>
                    <p className="text-slate-600 mb-8 max-w-md mx-auto">
                      Nous avons bien reçu votre demande et vous recontacterons très rapidement.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="btn-secondary"
                      data-testid="contact-new-message"
                    >
                      Envoyer un nouveau message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="form-label">Nom complet *</label>
                        <Input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Votre nom"
                          required
                          className="form-input"
                          data-testid="contact-input-name"
                        />
                      </div>
                      <div>
                        <label className="form-label">Email *</label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="votre@email.com"
                          required
                          className="form-input"
                          data-testid="contact-input-email"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="form-label">Téléphone *</label>
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="06 00 00 00 00"
                          required
                          className="form-input"
                          data-testid="contact-input-phone"
                        />
                      </div>
                      <div>
                        <label className="form-label">Type de service</label>
                        <Select onValueChange={handleServiceChange} value={formData.service_type}>
                          <SelectTrigger className="form-input" data-testid="contact-select-service">
                            <SelectValue placeholder="Sélectionnez un service" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="climatisation">Climatisation</SelectItem>
                            <SelectItem value="pompe-chaleur">Pompe à chaleur</SelectItem>
                            <SelectItem value="froid-commercial">Froid commercial</SelectItem>
                            <SelectItem value="ventilation">Ventilation</SelectItem>
                            <SelectItem value="depannage">Dépannage urgent</SelectItem>
                            <SelectItem value="autre">Autre</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <label className="form-label">Sujet *</label>
                      <Input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="Objet de votre demande"
                        required
                        className="form-input"
                        data-testid="contact-input-subject"
                      />
                    </div>

                    <div>
                      <label className="form-label">Message *</label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Décrivez votre projet ou votre besoin..."
                        required
                        rows={5}
                        className="form-input resize-none"
                        data-testid="contact-input-message"
                      />
                    </div>

                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="is_urgent"
                        name="is_urgent"
                        checked={formData.is_urgent}
                        onChange={handleChange}
                        className="w-5 h-5 rounded border-slate-300 text-[#fabc63] focus:ring-[#fabc63]"
                        data-testid="contact-checkbox-urgent"
                      />
                      <label htmlFor="is_urgent" className="text-slate-600">
                        <span className="font-medium">Demande urgente</span> (intervention rapide souhaitée)
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary w-full flex items-center justify-center gap-2 text-lg py-4"
                      data-testid="contact-submit"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          <span>Envoi en cours...</span>
                        </>
                      ) : (
                        <>
                          <Send size={20} />
                          <span>Envoyer ma demande</span>
                        </>
                      )}
                    </button>

                    <p className="text-sm text-slate-500 text-center">
                      En soumettant ce formulaire, vous acceptez d'être recontacté par ADM Confort Clim.
                    </p>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Google Maps Section */}
      <section className="py-12 bg-[#F8FAFC]" data-testid="contact-map-section">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-8"
            {...fadeInUp}
          >
            <h2 className="font-outfit text-2xl md:text-3xl font-bold text-[#000000] mb-2">
              Notre localisation
            </h2>
            <p className="text-slate-600">
              Basés à Moissy-Cramayel (77), nous intervenons en Île-de-France et dans toute la France
            </p>
          </motion.div>

          <motion.div 
            className="rounded-2xl overflow-hidden shadow-lg"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2636.987654321!2d2.5894!3d48.6247!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e5e1a0e1b4d3a5%3A0x40799119fb1b8e0!2s209%20Rue%20de%20Changis%2C%2077550%20Moissy-Cramayel!5e0!3m2!1sfr!2sfr!4v1702656000000!5m2!1sfr!2sfr"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Localisation ADM Confort Clim - 209 rue de Changis, 77550 Moissy-Cramayel"
              className="w-full"
            ></iframe>
          </motion.div>

          <motion.div 
            className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4"
            {...fadeInUp}
          >
            <a
              href="https://www.google.com/maps/dir//209+Rue+de+Changis,+77550+Moissy-Cramayel"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary flex items-center gap-2"
              data-testid="contact-map-directions"
            >
              <MapPin size={18} />
              Obtenir l'itinéraire
            </a>
          </motion.div>
        </div>
      </section>

      {/* Google Review CTA Section */}
      <section className="py-10 bg-white" data-testid="google-review-section">
        <div className="container-custom">
          <motion.div 
            className="bg-gradient-to-r from-[#1997b0]/5 to-[#fabc63]/5 rounded-2xl p-6 md:p-8"
            {...fadeInUp}
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-center md:text-left">
                <div className="w-14 h-14 bg-white rounded-full shadow-md flex items-center justify-center flex-shrink-0">
                  <svg viewBox="0 0 24 24" className="w-7 h-7" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-outfit text-lg md:text-xl font-bold text-[#000000] mb-1">
                    Satisfait de nos services ?
                  </h3>
                  <p className="text-slate-600 text-sm">
                    Votre avis nous aide à nous améliorer et à aider d'autres clients.
                  </p>
                </div>
              </div>
              <a
                href="https://share.google/fFoF35oPbjq0fGiqs"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-full shadow-md hover:shadow-lg transition-all border border-slate-200 group whitespace-nowrap"
                data-testid="leave-google-review"
              >
                <Star className="w-5 h-5 text-[#fabc63] fill-[#fabc63]" />
                <span className="font-medium text-slate-700 group-hover:text-[#1997b0] transition-colors">
                  Laisser un avis Google
                </span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1997b0]" data-testid="contact-cta">
        <div className="container-custom">
          <motion.div 
            className="text-center text-white"
            {...fadeInUp}
          >
            <h2 className="font-outfit text-3xl md:text-4xl font-bold mb-4">
              Besoin d'une intervention rapide ?
            </h2>
            <p className="text-white/80 mb-8 max-w-2xl mx-auto">
              Pour toute urgence, appelez-nous directement. Nous intervenons 24h/24 et 7j/7 
              en Île-de-France et partout en France selon vos besoins.
            </p>
            <a 
              href="tel:0603054911"
              className="btn-emergency inline-flex items-center gap-2 text-xl"
              data-testid="contact-final-cta"
            >
              <Phone size={24} />
              <span>06 03 05 49 11</span>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
